/**
 * Uitrusting - pure maat-helpers. Gedeeld tussen de portal-catalogus
 * (auto-selectie van de medewerker-maat), de maat-keuzelijsten op de
 * profielschermen en de API package-toekenning, zodat een opgeslagen
 * medewerker-maat overal identiek tegen de variant-maat matcht.
 *
 * -- Waarom canonicalisatie ------------------------------------------------
 * De medewerker-maat en de variant-maat zijn allebei vrije tekst met een eigen
 * herkomst (zelf ingevuld vs. leveranciers-import). Daardoor bestaan er
 * meerdere schrijfwijzen voor DEZELFDE maat:
 *
 *   - handschoen: "XL" (letter) vs "10" (EN 420-nummer)
 *   - broek:      "C48" (NL-confectie, C = normale lengte) vs "48"
 *   - kleding:    "XXL" vs "2XL" (staan in de catalogus zelfs naast elkaar)
 *   - bereik:     "44/45" ("past allebei")
 *
 * Een kale string-vergelijking maakt daar een dood spoor van: de monteur ziet
 * "Niet in jouw maat beschikbaar" terwijl het artikel gewoon in zijn maat
 * bestaat. Daarom brengen we BEIDE kanten eerst naar een canonieke vorm en
 * vergelijken we die. Diezelfde canonieke vorm is wat de maat-keuzelijst op de
 * profielschermen aanbiedt, zodat nieuwe invoer per definitie matcht.
 */

/** One-size / maatloze waarden: deze artikelen zijn voor iedereen beschikbaar. */
export const EEN_MAAT_SENTINELS = new Set([
  '',
  'os',
  'uni',
  'nvt',
  'n.v.t.',
  'one size',
  'onesize',
  'one-size',
])

const BREEDTE_WOORDEN = /\b(extra\s+breed|breed|smal|wijd|wide|narrow)\b/g

/**
 * Normaliseer een maat-string voor vergelijking: lowercase, trim, strip
 * notities tussen haakjes (bv. "(extra breed)"), strip losse breedte-/pasvorm-
 * woorden, strip een "maat"-prefix, komma-decimaal -> punt, collapse witruimte.
 */
export function normaliseerMaatVoorMatch(raw: string | null | undefined): string {
  if (raw == null) return ''
  return raw
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ') // notities tussen haakjes
    .replace(BREEDTE_WOORDEN, ' ') // losse breedte-/pasvorm-woorden
    .replace(/\bmaat\b/g, ' ') // "maat 43" -> "43"
    .replace(/,/g, '.') // 43,5 -> 43.5
    .replace(/\s+/g, ' ')
    .trim()
}

/** True voor one-size/maatloze waarden (OS, UNI, n.v.t., one-size, leeg, null). */
export function isMaatloos(maat: string | null | undefined): boolean {
  return EEN_MAAT_SENTINELS.has(normaliseerMaatVoorMatch(maat))
}

// --- Canonicalisatie -------------------------------------------------------

/**
 * NL-confectieprefix op broekmaten: "C48" is confectiemaat 48. De catalogus
 * voert die als kale "48", met losse "48 MINUS"/"48 PLUS"-varianten voor de
 * verkorte en verlengde pasvorm, dus de C hoort er bij het matchen af.
 *
 * Alleen strippen vóór cijfers: een losse "c" is geen maat en blijft staan
 * (die matcht dan niets, en de medewerker kiest zelf een maat in de portal).
 */
const CONFECTIE_C_PREFIX = /^c(?=\d)/

/**
 * Letter-maten met herhaalde X naar de tel-notatie: xxl -> 2xl, xxxl -> 3xl,
 * xxs -> 2xs. De catalogus gebruikt beide schrijfwijzen door elkaar.
 */
const HERHAALDE_X = /^(x{2,6})(s|l)$/

/**
 * Handschoen-letter -> EN 420-nummer. Dit is de standaard-equivalentie die
 * leveranciers (OXXA, Pehavo) op hun maattabellen voeren; de catalogus voert
 * uitsluitend de nummers.
 */
const HANDSCHOEN_LETTER_NAAR_NUMMER: Readonly<Record<string, string>> = {
  xs: '6',
  s: '7',
  m: '8',
  l: '9',
  xl: '10',
  '2xl': '11',
  '3xl': '12',
}

/** Omgekeerde van HANDSCHOEN_LETTER_NAAR_NUMMER, voor de label-hint "10 (XL)". */
const HANDSCHOEN_NUMMER_NAAR_LETTER: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(HANDSCHOEN_LETTER_NAAR_NUMMER).map(([letter, nummer]) => [nummer, letter]),
)

function canoniekeEnkeleMaat(deel: string, maatType?: string | null): string {
  const type = (maatType ?? '').trim().toLowerCase()
  const zonderPrefix = type === 'broek' ? deel.replace(CONFECTIE_C_PREFIX, '') : deel
  const herhaald = HERHAALDE_X.exec(zonderPrefix)
  const waarde = herhaald ? `${herhaald[1]!.length}x${herhaald[2]!}` : zonderPrefix
  if (type === 'handschoen') return HANDSCHOEN_LETTER_NAAR_NUMMER[waarde] ?? waarde
  return waarde
}

/**
 * Canonieke vorm(en) van een maat. Meervoud omdat een bereik-notatie ("44/45")
 * twee maten dekt: de drager past allebei, dus beide tellen als match.
 * Lege/onbekende invoer geeft een lege lijst (matcht nooit).
 */
export function canoniekeMaten(raw: string | null | undefined, maatType?: string | null): string[] {
  const basis = normaliseerMaatVoorMatch(raw)
  if (basis.length === 0) return []
  const delen = basis.includes('/')
    ? basis
        .split('/')
        .map((d) => d.trim())
        .filter((d) => d.length > 0)
    : [basis]
  const uniek = new Set<string>()
  for (const deel of delen) uniek.add(canoniekeEnkeleMaat(deel, maatType))
  return [...uniek]
}

/**
 * Matcht een medewerker-maat tegen een variant-maat: beide naar canonieke vorm,
 * dan vergelijken. `maatType` is optioneel maar sterk aangeraden - zonder type
 * kan "M" niet onderscheiden worden tussen bovenkleding (M) en handschoen (8).
 * Lege waarden matchen nooit (een onbekende maat is geen match).
 */
export function maatMatcht(
  medewerkerMaat: string | null | undefined,
  variantMaat: string | null | undefined,
  maatType?: string | null,
): boolean {
  const eigen = canoniekeMaten(medewerkerMaat, maatType)
  const variant = canoniekeMaten(variantMaat, maatType)
  if (eigen.length === 0 || variant.length === 0) return false
  return eigen.some((m) => variant.includes(m))
}

// --- Keuzelijst (profielschermen) ------------------------------------------

/** Vaste volgorde van letter-maten, klein naar groot. */
const LETTER_VOLGORDE = [
  '3xs',
  '2xs',
  'xs',
  's',
  'm',
  'l',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
  '7xl',
]

/**
 * Sorteersleutel: eerst numerieke maten oplopend (46 < 46 minus < 48), daarna
 * letter-maten in confectievolgorde, daarna de rest alfabetisch.
 */
function sorteerSleutel(maat: string): [number, number, string] {
  // Letter-maten eerst herkennen: "2xl" begint met een cijfer en zou anders als
  // numerieke maat 2 vooraan sorteren.
  const index = LETTER_VOLGORDE.indexOf(maat)
  if (index >= 0) return [1, index, maat]
  const numeriek = /^(\d+(?:\.\d+)?)/.exec(maat)
  if (numeriek) {
    const waarde = Number.parseFloat(numeriek[1]!)
    // Confectiematen (36-67) horen bovenaan; drie-cijferige kindermaten
    // (128-176 bij bovenkleding) zijn een randgeval en horen niet vóór S/M/L.
    return waarde >= 100 ? [2, waarde, maat] : [0, waarde, maat]
  }
  return [3, 0, maat]
}

function vergelijkMaten(a: string, b: string): number {
  const [groepA, nummerA, tekstA] = sorteerSleutel(a)
  const [groepB, nummerB, tekstB] = sorteerSleutel(b)
  if (groepA !== groepB) return groepA - groepB
  if (nummerA !== nummerB) return nummerA - nummerB
  return tekstA.localeCompare(tekstB, 'nl')
}

/** Weergave-vorm van een canonieke maat: letters in hoofdletters, cijfers as-is. */
export function toonMaat(canoniek: string): string {
  return canoniek.replace(/[a-z]/g, (c) => c.toUpperCase())
}

/**
 * Label voor de keuzelijst. Bij handschoenen tonen we de letter-equivalent als
 * hint ("10 (XL)"), omdat medewerkers hun handschoenmaat vaak als letter kennen
 * terwijl de catalogus op nummer bestelt.
 */
export function maatOptieLabel(canoniek: string, maatType?: string | null): string {
  const weergave = toonMaat(canoniek)
  if ((maatType ?? '').trim().toLowerCase() === 'handschoen') {
    const letter = HANDSCHOEN_NUMMER_NAAR_LETTER[canoniek]
    if (letter) return `${weergave} (${toonMaat(letter)})`
  }
  return weergave
}

export interface MaatOptie {
  /** Canonieke opslagwaarde, bv. "10", "48", "2XL". */
  waarde: string
  /** Weergave in de keuzelijst, bv. "10 (XL)". */
  label: string
}

/**
 * Bouwt de keuzelijst voor een maatType uit de maten die de catalogus
 * daadwerkelijk voert: canonicaliseren (zodat "XXL" en "2XL" een optie worden),
 * ontdubbelen, maatloze sentinels weglaten en sorteren. Wat hier niet in staat,
 * kan niemand bestellen - dus staat het ook niet in de keuzelijst.
 */
export function bouwMaatOpties(
  variantMaten: ReadonlyArray<string | null | undefined>,
  maatType: string,
): MaatOptie[] {
  const uniek = new Set<string>()
  for (const maat of variantMaten) {
    if (isMaatloos(maat)) continue
    for (const canoniek of canoniekeMaten(maat, maatType)) uniek.add(canoniek)
  }
  return [...uniek]
    .sort(vergelijkMaten)
    .map((waarde) => ({ waarde: toonMaat(waarde), label: maatOptieLabel(waarde, maatType) }))
}

/**
 * Dekt de canonieke vorm de HELE opgeslagen waarde, of gaat er informatie
 * verloren? Twee soorten verlies:
 *
 *   "45 (extra breed)" -> "45"      de pasvorm-notitie valt weg
 *   "44/45"            -> "44"      de tweede maat van het bereik valt weg
 *
 * Dat verschil bepaalt of we een opgeslagen maat mogen herschrijven. "C48" en
 * "XXL" zijn wél eenduidig: daar is de canonieke vorm puur een andere
 * schrijfwijze van dezelfde maat, en herschrijven verliest niets.
 */
export function maatIsEenduidig(raw: string | null | undefined, maatType?: string | null): boolean {
  const waarde = (raw ?? '').trim()
  if (waarde.length === 0) return true
  // Alles wat `normaliseerMaatVoorMatch` méér doet dan case/witruimte/decimaal-
  // teken gelijktrekken, is weggestripte informatie.
  const kaal = waarde
    .toLowerCase()
    .replace(/,/g, '.')
    .replace(/\s+/g, ' ')
    .trim()
  if (normaliseerMaatVoorMatch(waarde) !== kaal) return false
  return canoniekeMaten(waarde, maatType).length === 1
}

/**
 * Zet een opgeslagen maat om naar de bijbehorende waarde uit de keuzelijst.
 * "C48" wordt zo "48" zodra iemand het formulier opent: het select-veld toont
 * de juiste selectie (anders zou het leeg lijken en bij opslaan wissen).
 *
 * Herschrijft UITSLUITEND een eenduidige waarde. "45 (extra breed)" en "44/45"
 * blijven staan zoals ze zijn: die dragen informatie die de canonieke vorm niet
 * kan bevatten, en die hoort naar `maatOpmerking` te verhuizen (zie het
 * normaliseer-script) in plaats van bij het openen van een formulier stil te
 * verdwijnen.
 */
export function maatNaarOptieWaarde(
  raw: string | null | undefined,
  opties: ReadonlyArray<{ waarde: string }>,
  maatType?: string | null,
): string {
  const waarde = (raw ?? '').trim()
  if (waarde.length === 0) return ''
  if (!maatIsEenduidig(waarde, maatType)) return waarde
  const match = opties.find((o) => maatMatcht(waarde, o.waarde, maatType))
  return match ? match.waarde : waarde
}

/**
 * Voert de catalogus deze maat (canoniek vergeleken)? Voedt het signaal in de
 * backoffice-matenlijst: wie hier `false` scoort kan in de portal niets in dat
 * maatType bestellen. Leeg = "nog niet ingevuld", geen fout-signaal.
 */
export function isMaatLeverbaar(
  raw: string | null | undefined,
  opties: ReadonlyArray<{ waarde: string }>,
  maatType?: string | null,
): boolean {
  const waarde = (raw ?? '').trim()
  if (waarde.length === 0) return true
  if (opties.length === 0) return true
  return opties.some((o) => maatMatcht(waarde, o.waarde, maatType))
}

/**
 * Hoe een maatveld zich moet gedragen. Eén bron voor portal én backoffice: de
 * beslissing keuzelijst-of-tekstveld hangt UITSLUITEND af van het vocabulaire,
 * niet van de huidige waarde. Anders zou een medewerker met een ingevulde maat
 * en een leeg vocabulaire een dropdown met één dode optie krijgen, en zou
 * intypen in het tekstveld na één teken omslaan naar een select.
 *
 * De eigen waarde wordt wél als extra optie toegevoegd zodra hij niet letterlijk
 * in de lijst staat - nooit stil wissen. Het "(niet leverbaar)"-label hangt aan
 * de canonieke toets: "44/45" matcht wél een variant en heet dus gewoon "44/45".
 */
export interface MaatVeldModus {
  modus: 'keuze' | 'vrij'
  opties: MaatOptie[]
}

export function maatVeldModus(
  vocabulaire: ReadonlyArray<MaatOptie>,
  huidig: string | null | undefined,
  maatType?: string | null,
): MaatVeldModus {
  if (vocabulaire.length === 0) return { modus: 'vrij', opties: [] }
  const waarde = (huidig ?? '').trim()
  const bekend =
    waarde.length === 0 ||
    vocabulaire.some((o) => o.waarde.toLowerCase() === waarde.toLowerCase())
  if (bekend) return { modus: 'keuze', opties: [...vocabulaire] }
  const label = isMaatLeverbaar(waarde, vocabulaire, maatType)
    ? waarde
    : `${waarde} (niet leverbaar)`
  return { modus: 'keuze', opties: [...vocabulaire, { waarde, label }] }
}

// --- Medewerker-maatprofiel ------------------------------------------------

/** Maat-profiel van een medewerker: de vijf opgeslagen maat-velden. */
export interface MaatProfiel {
  kledingmaatBoven: string | null
  kledingmaatBroek: string | null
  schoenmaat: string | null
  handschoenmaat: string | null
  helmmaat: string | null
}

/**
 * maatType -> de bijbehorende medewerker-maat. Gedeeld tussen de portal-
 * catalogus en de API package-toekenning (één bron van waarheid). maatType
 * wordt case-ongevoelig vergeleken.
 */
export function maatVoorType(
  maatType: string | null | undefined,
  profiel: MaatProfiel | null,
): string | null {
  if (!profiel) return null
  switch ((maatType ?? '').trim().toLowerCase()) {
    case 'boven':
      return profiel.kledingmaatBoven
    case 'broek':
      return profiel.kledingmaatBroek
    case 'schoen':
      return profiel.schoenmaat
    case 'handschoen':
      return profiel.handschoenmaat
    case 'helm':
      return profiel.helmmaat
    default:
      return null
  }
}
